import { Button, Checkbox, Col, Row } from 'antd';
import React, { FC, Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import apiPaths from 'services/apiPaths';
import { ExperienceDetailWrapper } from 'src/components/challenge/ExperienceDetailWrapper';
import CustomModal from 'src/components/CustomModal';
import { IRootReducers } from 'src/reducers';
import {
  ButtonStatusEnum,
  ChallengeTypeEnum,
  ExperienceDetailStatusEnum,
} from 'src/shared/enums';
import {
  IChallengeRedeem,
  IChallengeResourceRender,
  IExperienceDetailWrapper,
} from 'src/shared/models';
import {
  checkUserResourcePoints,
  completeChallengeUserResource,
  customFormatMessage,
  dispatchCustomEvent,
  slugify,
} from 'utils';

interface OwnProps extends IChallengeResourceRender {
  config: IChallengeRedeem;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ChallengeRedeemProduct: FC<Props> = ({
  challenge,
  config,
  updateChallenge,
  updateUserPoints,
  currentUserPoints,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const userPoints = currentUserPoints || 0;
  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    config.points,
    challenge?.challengeType,
  );

  const isExperience =
    challenge?.challengeType === ChallengeTypeEnum.EXPERIENCE && config;

  const { formatMessage } = useIntl();
  const { points, title, buttonText, termsLink, termsText, descriptionText } =
    config;

  const labelPoints = Math.abs(config.points);

  const dispatchCustomEventLegal = (action: string) => {
    isExperience &&
      dispatchCustomEvent({
        eventCategory: `experience-${challenge.challengeId.toString()}`,
        eventAction: action,
        eventLabel: 'legal-bases',
      });
  };

  const handleOpenModal = () => {
    if (isExperience) {
      const eventStatus = hasUserEnoughPoints ? 'ok' : 'not-enough-points';
      dispatchCustomEvent({
        eventAction: 'request',
        eventCategory: 'experience',
        eventDetail: slugify(challenge.challengeName),
        eventLabel: eventStatus,
        eventName: `redeem-points-${eventStatus}`,
      });
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setError(null);
  };

  const handleTermsCheckbox = () => {
    dispatchCustomEventLegal('accept');
    setTermsAccepted((termsAccepted) => !termsAccepted);
  };

  const handleTermsLink = () => {
    dispatchCustomEventLegal('review');
  };

  const performAction = async () => {
    if (!hasUserEnoughPoints) {
      handleCloseModal();
      return;
    }

    try {
      setLoading(true);
      const err = await completeChallengeUserResource({
        dataPath: apiPaths.CHALLENGES.COMPLETE_EXPERIENCE,
        idResource: config.idResource,
        updateChallenge,
        updateUserPoints,
      });
      if (err) {
        const stringError: string = formatMessage({
          id: 'experience.error-redeem-product',
        });
        setError(stringError);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const wrapperConfig: IExperienceDetailWrapper = {
    title,
    points,
    status: ExperienceDetailStatusEnum.IN_PROGRESS,
    button: {
      label: buttonText,
      status: ButtonStatusEnum.ENABLED,
      performAction: handleOpenModal,
    },
  };

  if (config.isCompleted) {
    wrapperConfig.button = {
      label: formatMessage({
        id: 'experience.button-completed',
      }),
      performAction: null,
      status: ButtonStatusEnum.DONE,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DONE;
  }

  if (config.isBlocked) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.DISABLED,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DISABLED;
  }

  if (!config.isChallengeInDate) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.HIDDEN,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.HIDDEN;
  }

  const preSubmitModalContent = () => {
    if (!hasUserEnoughPoints)
      return (
        <Row justify="center" align="middle">
          <Col sm={24} md={20}>
            <Row justify="center" className="block--small">
              <p className="experience-modal__text">
                {customFormatMessage('experience.exchange-not-enough', {
                  userPoints,
                  points: labelPoints,
                })}
              </p>
            </Row>
            <Row>
              <Button
                id="redeem-points-not-enough-points"
                onClick={() => setModalVisible(false)}
                className="btn-primary btn-primary--black experience-modal__button"
                loading={loading}
                block>
                {formatMessage({
                  id: 'button.understood',
                })}
              </Button>
            </Row>
          </Col>
        </Row>
      );

    return (
      <Row justify="center" align="middle">
        <Col sm={24} md={20}>
          <Row justify="center" className="block--small">
            <h5 className="experience-modal__title">
              {customFormatMessage('experience.exchange-points', {
                userPoints,
                points: labelPoints,
              })}
            </h5>
          </Row>
          <Row
            className="experience-modal__checkbox"
            justify="center"
            align="middle">
            <Checkbox
              checked={termsAccepted}
              className="checkbox-remember-login"
              onChange={handleTermsCheckbox}
            />
            {termsText ? (
              <span className="experience-modal__terms">
                <FormattedMessage id="experience.redeem-custom-terms" />{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={termsLink ?? ''}
                  onClick={handleTermsLink}
                  className="link underline-text">
                  {termsText}
                </a>
              </span>
            ) : (
              <span className="experience-modal__terms">
                {customFormatMessage('experience.redeem-terms', {
                  // eslint-disable-next-line react/display-name
                  terms: (chunks: any) => (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={termsLink ?? ''}
                      onClick={handleTermsLink}
                      className="link underline-text">
                      {chunks}
                    </a>
                  ),
                })}
              </span>
            )}
          </Row>
          <Row>
            <Button
              id="redeem-points-ok"
              onClick={performAction}
              className="btn-primary btn-primary--black experience-modal__button"
              loading={loading}
              disabled={!termsAccepted}
              block>
              {formatMessage({
                id: 'experience.use-my-points',
              })}
            </Button>
          </Row>
        </Col>
      </Row>
    );
  };

  const postSubmitModalContent = (
    <Col>
      <div className="icon icon--process-check experience-modal__icon" />
      {descriptionText ? (
        <span className="experience-modal__text experience-modal__text--success ">
          {descriptionText}
        </span>
      ) : (
        <span className="experience-modal__text experience-modal__text--success ">
          {customFormatMessage('experience.success-redeem-product')}
        </span>
      )}
    </Col>
  );

  if (!isExperience) return <Fragment />;

  return (
    <>
      <ExperienceDetailWrapper {...wrapperConfig}>
        <Fragment />
      </ExperienceDetailWrapper>
      <CustomModal
        {...{
          visible: modalVisible,
          loading,
          error,
          content: config.isCompleted
            ? postSubmitModalContent
            : preSubmitModalContent(),
          onCancel: handleCloseModal,
          footer: null,
          showIcon: false,
          customClass: 'quiz-modal-finalize',
        }}
      />
    </>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    currentUserPoints: state.auth.userPoints,
  };
};

export default connect(mapStateToProps)(ChallengeRedeemProduct);
